import React, { useRef } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { MrbButton } from "mrb/components/button";

const MAX_SESSION_BYTES = 1024 * 1024 * 1024;
const MAX_FILES = 50;

function formatBytes(bytes) {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function formatReason(reason) {
    switch (reason) {
        case "invalid-magic-bytes":
            return "Not a valid file";
        case "exceeds-size-limit":
            return "Exceeds size limit";
        case "too-many-files":
            return "Too many files";
        default:
            return reason;
    }
}

function isImageFile(filename) {
    return /\.(jpg|jpeg|png|gif|tiff|tif|webp)$/i.test(filename);
}

const ArtistUploadTemplate = observer(function ArtistUploadTemplate({ artistUploadViewStore, t }) {
    const { phase } = artistUploadViewStore;

    if (phase === "uploading") return <UploadingPhase artistUploadViewStore={artistUploadViewStore} />;
    if (phase === "pending") return <PendingPhase artistUploadViewStore={artistUploadViewStore} t={t} />;
    if (phase === "result") return <ResultPhase artistUploadViewStore={artistUploadViewStore} t={t} />;
    return <IdlePhase artistUploadViewStore={artistUploadViewStore} t={t} />;
});

const IdlePhase = observer(function IdlePhase({ artistUploadViewStore, t }) {
    const {
        artistName, albumName, description, files, error,
        setArtistName, setAlbumName, setDescription, setFiles, sendWithoutUpload, startUpload, removeFile,
    } = artistUploadViewStore;
    const fileInputRef = useRef(null);
    const totalBytes = files.reduce((acc, f) => acc + f.size, 0);
    const oversized = totalBytes > MAX_SESSION_BYTES;
    const tooManyFiles = files.length > MAX_FILES;
    const descriptionLength = description.length;
    const descriptionOverLimit = descriptionLength > 4000;

    return (
        <div>
            <div className="row">
                <div className="col col-sml-12 col-lrg-6">

                    {/* Section 1 — Content Info */}
                    <div className="c-artist-upload__section">
                        <h3 className="c-artist-upload__section-title">Content Info</h3>

                        <div className="c-form__field">
                            <label className="c-form__field__label" htmlFor="artist-name-input">Artist Name</label>
                            <input
                                id="artist-name-input"
                                type="text"
                                className="c-input c-input--text c-input--med c-input--outline"
                                value={artistName}
                                placeholder="Artist name"
                                onChange={(e) => setArtistName(e.target.value)}
                            />
                        </div>

                        <div className="c-form__field">
                            <label className="c-form__field__label" htmlFor="album-name-input">Album Name</label>
                            <input
                                id="album-name-input"
                                type="text"
                                className="c-input c-input--text c-input--med c-input--outline"
                                value={albumName}
                                placeholder="Album name"
                                onChange={(e) => setAlbumName(e.target.value)}
                            />
                        </div>

                        <div className="c-form__field">
                            <label className="c-form__field__label" htmlFor="description-input">
                                Cloud Link to Content / Notes
                            </label>
                            <textarea
                                id="description-input"
                                className="c-input c-input--text c-input--med c-input--outline"
                                style={{ resize: "vertical", minHeight: "80px", width: "100%" }}
                                rows={5}
                                value={description}
                                placeholder="Add Dropbox, Google Drive, WeTransfer etc. links to downloadable files here. If you are uploading files below, use this field for revenue sharing info or other applicable notes."
                                onChange={(e) => setDescription(e.target.value)}
                            />
                            <p
                                className="u-type--sml u-mar--top--tny"
                                style={{ color: descriptionOverLimit ? "var(--color-warning, #f44336)" : undefined }}
                            >
                                {descriptionLength} / 4000
                            </p>
                        </div>

                        {error && (
                            <div className="c-validation c-validation--error u-mar--bottom--med">
                                <span className="c-validation__label">{error}</span>
                            </div>
                        )}

                        <MrbButton
                            t={t}
                            className="c-btn c-btn--base c-btn--primary"
                            onClick={sendWithoutUpload}
                            disabled={!artistName.trim() || !albumName.trim() || !description.trim() || descriptionOverLimit}
                            label="Send without file upload"
                        />
                    </div>

                    {/* Divider */}
                    <hr className="c-artist-upload__section-divider" />

                    {/* Section 2 — Files */}
                    <div className="c-artist-upload__section">
                        <h3 className="c-artist-upload__section-title">Files</h3>

                        <div className="c-form__field">
                            <label className="c-form__field__label">
                                Files
                                <span className="u-type--color--secondary u-mar--left--tny">
                                    (max {MAX_FILES} files, 1 GB total)
                                </span>
                            </label>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".mp3,.wav,.jpg,.jpeg,.png,.gif,.tiff,.tif,.webp"
                                multiple
                                className="u-display--none"
                                onChange={(e) => { setFiles(e.target.files); e.target.value = ""; }}
                            />
                            <div>
                                <MrbButton
                                    t={t}
                                    className="c-btn c-btn--base c-btn--secondary"
                                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                    label="Choose Files"
                                />
                            </div>
                        </div>

                        {files.length > 0 && (
                            <div>
                                <p className="c-artist-upload__file-total">
                                    {files.length} file{files.length !== 1 ? "s" : ""} — {formatBytes(totalBytes)}
                                    {tooManyFiles && (
                                        <span className="u-type--color--warning"> (exceeds {MAX_FILES} file limit)</span>
                                    )}
                                    {!tooManyFiles && oversized && (
                                        <span className="u-type--color--warning"> (exceeds 1 GB limit)</span>
                                    )}
                                </p>
                                <div className="c-artist-upload__file-list">
                                    {files.map((f) => (
                                        <div
                                            key={f.name}
                                            className={`c-artist-upload__file-row${isImageFile(f.name) ? " c-artist-upload__file-row--image" : ""}`}
                                        >
                                            <i
                                                className={`u-icon u-icon--base ${isImageFile(f.name) ? "u-icon--image-replace" : "u-icon--music-note"}`}
                                            />
                                            <div className="c-artist-upload__file-info">
                                                <span className="c-artist-upload__file-name">{f.name}</span>
                                                <span className="c-artist-upload__file-size">{formatBytes(f.size)}</span>
                                            </div>
                                            <button
                                                type="button"
                                                className="c-artist-upload__file-remove"
                                                onClick={() => removeFile(f.name)}
                                                aria-label="Remove file"
                                            >
                                                <i className="u-icon u-icon--sml u-icon--close" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <MrbButton
                            t={t}
                            className="c-btn c-btn--base c-btn--primary"
                            onClick={startUpload}
                            disabled={!files.length || tooManyFiles || oversized || !artistName.trim() || !albumName.trim() || descriptionOverLimit}
                            label="Upload & Send"
                        />
                    </div>

                </div>
            </div>
        </div>
    );
});

const PendingPhase = observer(function PendingPhase({ artistUploadViewStore, t }) {
    const { pendingSession, isFinalizing, finalizeSession } = artistUploadViewStore;
    if (!pendingSession) return null;

    return (
        <div className="row">
            <div className="col col-sml-12 col-lrg-6">
                <div className="c-artist-upload__uploading-header u-mar--bottom--sml">
                    <span className="c-artist-upload__uploading-dot" />
                    Upload in progress
                </div>

                <div className="c-artist-upload__pending-meta u-mar--bottom--sml">
                    <p className="u-type--base u-type--wgt--bold">{pendingSession.artistName}</p>
                    <p className="u-type--sml u-type--color--secondary">{pendingSession.albumName}</p>
                </div>

                {pendingSession.expectedFileNames.length > 0 && (
                    <div className="c-artist-upload__file-list u-mar--bottom--med">
                        {pendingSession.expectedFileNames.map((name) => (
                            <div
                                key={name}
                                className={`c-artist-upload__file-row${isImageFile(name) ? " c-artist-upload__file-row--image" : ""}`}
                            >
                                <i className={`u-icon u-icon--base ${isImageFile(name) ? "u-icon--image-replace" : "u-icon--music-note"}`} />
                                <div className="c-artist-upload__file-info">
                                    <span className="c-artist-upload__file-name">{name}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <p className="u-type--sml u-type--color--secondary u-mar--bottom--med">
                    Files are uploading in the background and the result will appear automatically. If the upload seems stuck, click <strong>Check Status</strong> — if something went wrong you will be returned to the upload form.
                </p>

                <MrbButton
                    t={t}
                    className="c-btn c-btn--base c-btn--primary"
                    onClick={finalizeSession}
                    disabled={isFinalizing}
                    label={isFinalizing ? "Checking..." : "Check Status"}
                />
            </div>
        </div>
    );
});

const UploadingPhase = observer(function UploadingPhase({ artistUploadViewStore }) {
    const { files, fileProgresses } = artistUploadViewStore;
    const doneCount = files.filter((f) => (fileProgresses[f.name] || 0) === 100).length;

    return (
        <div className="row">
            <div className="col col-sml-12 col-lrg-6">
                <div className="c-artist-upload__uploading-header">
                    <span className="c-artist-upload__uploading-dot" />
                    Uploading {files.length} file{files.length !== 1 ? "s" : ""}
                    {doneCount > 0 && (
                        <span className="u-type--color--secondary">
                            &nbsp;— {doneCount} of {files.length} done
                        </span>
                    )}
                </div>
                {doneCount === files.length && files.length > 0 && (
                    <p className="u-type--sml u-type--color--secondary u-mar--bottom--med">
                        Files are processing, please wait…
                    </p>
                )}
                {files.map((f) => {
                    const progress = fileProgresses[f.name] || 0;
                    const done = progress === 100;
                    return (
                        <div key={f.name} className="c-artist-upload__upload-item">
                            <div className="c-artist-upload__upload-item-header">
                                <span className="c-artist-upload__upload-item-name">{f.name}</span>
                                <span className={`c-artist-upload__upload-item-pct${done ? " c-artist-upload__upload-item-pct--done" : ""}`}>
                                    {progress}%
                                </span>
                            </div>
                            <div className="c-progressbar">
                                <div className="c-progressbar__wrapper">
                                    <div
                                        className={`c-progressbar__range ${done ? "is-finished" : "in-progress"}`}
                                        style={{ width: `${progress}%`, transition: "width 0.3s ease" }}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

const ResultPhase = observer(function ResultPhase({ artistUploadViewStore, t }) {
    const { result, reset } = artistUploadViewStore;
    if (!result) return null;

    const accepted = result.accepted || [];
    const rejected = result.rejected || [];
    const processedCount = accepted.length + rejected.length;
    const interrupted = result.expectedCount != null && !result.isSendOnly && processedCount < result.expectedCount;

    return (
        <div className="row">
            <div className="col col-sml-12 col-lrg-6">
                {result.isSendOnly && (
                    <div className="c-validation c-validation--success u-mar--bottom--med">
                        <span className="c-validation__label">Content info sent successfully.</span>
                    </div>
                )}
                {interrupted && (
                    <div className="c-validation c-validation--warning u-mar--bottom--med">
                        <span className="c-validation__label">
                            Only {processedCount} of {result.expectedCount} files were processed — the upload was likely interrupted (e.g. by a page refresh). Please upload the remaining files again.
                        </span>
                    </div>
                )}
                {!result.isSendOnly && (
                    <div className="c-artist-upload__result-summary">
                        <div className="c-artist-upload__result-stat c-artist-upload__result-stat--success">
                            <span className="c-artist-upload__result-stat-count c-artist-upload__result-stat-count--success">{accepted.length}</span>
                            <span className="c-artist-upload__result-stat-label c-artist-upload__result-stat-label--success">Accepted</span>
                        </div>
                        {rejected.length > 0 && (
                            <div className="c-artist-upload__result-stat c-artist-upload__result-stat--error">
                                <span className="c-artist-upload__result-stat-count c-artist-upload__result-stat-count--error">{rejected.length}</span>
                                <span className="c-artist-upload__result-stat-label c-artist-upload__result-stat-label--error">Rejected</span>
                            </div>
                        )}
                    </div>
                )}

                {accepted.length > 0 && (
                    <div className="c-artist-upload__result-section">
                        <p className="c-artist-upload__result-section-title c-artist-upload__result-section-title--success">
                            Accepted files
                        </p>
                        {accepted.map((fileName) => (
                            <div key={fileName} className="c-artist-upload__result-row c-artist-upload__result-row--success">
                                <div className="c-artist-upload__result-row-header">
                                    <i className="u-icon u-icon--base u-icon--checked" />
                                    <span className="c-artist-upload__result-row-name">{fileName}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {rejected.length > 0 && (
                    <div className="c-artist-upload__result-section">
                        <p className="c-artist-upload__result-section-title c-artist-upload__result-section-title--error">
                            Rejected files
                        </p>
                        {rejected.map((r) => (
                            <div key={r.fileName} className="c-artist-upload__result-row c-artist-upload__result-row--error">
                                <div className="c-artist-upload__result-row-header">
                                    <i className="u-icon u-icon--base u-icon--close--error" />
                                    <span className="c-artist-upload__result-row-name">{r.fileName}</span>
                                </div>
                                <span className="c-artist-upload__result-row-reason">{formatReason(r.reason)}</span>
                            </div>
                        ))}
                    </div>
                )}

                <MrbButton
                    t={t}
                    className="c-btn c-btn--base c-btn--secondary"
                    onClick={reset}
                    label="Upload More"
                />
            </div>
        </div>
    );
});

ArtistUploadTemplate.propTypes = {
    artistUploadViewStore: PropTypes.object.isRequired,
    t: PropTypes.func,
};

export default ArtistUploadTemplate;
